import React, { useState, useEffect, useRef } from 'react';
import {
  Col,
  Button,
  Card,
  Modal,
  Form,
} from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import qs from 'qs';
import UserIcon from '../../common/components/UserIcon';
import PeopleLayout from '../layout/PeopleLayout';
import { Icon, LoadingAnimation } from '../../common/components/Utilities';

function FeedbackTopicPage(props) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [sortBy, setSortBy] = useState('hot');

  const header = (
    <TopicHeader
      {...props}
      setLoading={setLoading}
      setItems={setItems}
      setSearchString={setSearchString}
      sortBy={sortBy}
      setSortBy={setSortBy}
    />
  );

  const content = (
    <FeedbackTopic
      {...props}
      loading={loading}
      setLoading={setLoading}
      items={items}
      setItems={setItems}
      search={searchString}
      sort={sortBy}
    />
  );

  return (
    <PeopleLayout
      {...props}
      header={header}
      content={content}
    />
  );
}

function TopicHeader({
  topic, setItems, setLoading, setSearchString, setSortBy,
}) {
  const [showAddFeedback, setShowAddFeedback] = useState(false);
  const searchRef = useRef();

  const addFeedbackClick = (event) => {
    event.preventDefault();
    setShowAddFeedback(true);
  };

  const debounce = (func, wait) => {
    let timeout;

    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const onSearchChange = debounce(() => {
    setSearchString(searchRef.current.value);
  }, 200);

  return (
    <>
      <div className="feature-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <h1>{topic.title}</h1>
            </div>
          </div>
          <div className="row">
            <div className="col-12" style={{ fontSize: '1.4em' }}>
              <Card>
                <Card.Body>
                  {topic.description}
                </Card.Body>
              </Card>
            </div>
          </div>

          <div className="row mt-4">
            <Col md={3}>
              <input className="form-control" type="text" ref={searchRef} onChange={onSearchChange} placeholder="Search for feedback..." />
            </Col>
            <Col md={3}>
              <select className="form-control" onChange={(event) => setSortBy(event.target.value)}>
                <option value="hot">Sort by Hot</option>
                <option value="top">Sort by Most Votes</option>
                <option value="new">Sort by New</option>
              </select>
            </Col>
            <Col md={3} />
            <Col md={3}>
              <Button variant="secondary" block onClick={addFeedbackClick}>
                <Icon icon="comments-alt" variant="fad" />
                {' '}
                Add Feedback
              </Button>
            </Col>
          </div>
        </div>
      </div>

      <AddFeedback
        topic={topic}
        showAddFeedback={showAddFeedback}
        setShowAddFeedback={setShowAddFeedback}
        setLoading={setLoading}
        setItems={setItems}
      />
    </>
  );
}

export function FeedbackTopic({
  topic, loading, setLoading, items, setItems, search, sort,
}) {
  const loadItems = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`/feedback/${topic.slug}/items`, {
        params: {
          q: search,
          sort,
        },
        paramsSerializer: (params) => qs.stringify(params),
      });

      setItems(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, [topic, search, sort]);

  let noResults;

  if (items.length === 0 && !loading) {
    noResults = (
      <tr>
        <td colSpan={2}>
          <div className="alert alert-warning m-4">
            <Icon icon="frown" append="mr-1" />
            {' '}
            <strong>Sorry!</strong>
            {' '}
            Despite our best efforts we could not find any matching feedback.
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <LoadingAnimation loading={loading} />
      <table className="table">
        <tbody>
          {noResults}
          {items.map((item) => <FeedbackItemSummary key={item.id} topic={topic} item={item} />)}
        </tbody>
      </table>
    </div>
  );
}

function FeedbackItemSummary({ topic, item }) {
  let badge;

  switch (item.type) {
    case 'suggestion':
      badge = (
        <span className="badge badge-info">
          <Icon icon="lightbulb-on" />
          {' '}
          Suggestion or Idea
        </span>
      );
      break;

    case 'positive':
      badge = (
        <span className="badge badge-success">
          <Icon icon="smile" />
          {' '}
          Positive Feedback
        </span>
      );
      break;

    case 'negative':
      badge = (
        <span className="badge badge-danger">
          <Icon icon="frown" />
          {' '}
          Constructive Criticism
        </span>
      );
      break;

    default:
      badge = '';
      break;
  }

  return (
    <tr>
      <td style={{ width: 1, paddingTop: '20px', paddingBottom: '20px' }}>
        <FeedbackVotes topic={topic} item={item} />
      </td>
      <td style={{ paddingTop: '20px', paddingBottom: '20px' }}>
        <div style={{ float: 'right' }}>{badge}</div>
        <div><strong>{item.title}</strong></div>
        <div className="text-muted">{item.description}</div>
        <div className="text-muted mt-3" style={{ fontSize: '0.8em' }}>
          Posted
          {moment(item.created_at).format('dddd D MMM, h:mma')}
          <div className="mt-1"><FeedbackSignatories item={item} /></div>
        </div>
      </td>
    </tr>
  );
}

function FeedbackSignatories({ item }) {
  const [thisItem, setItem] = useState(item);

  let button;

  const sign = (event) => {
    event.preventDefault();

    axios.post(`/feedback/${item.id}/sign`, { _token: window.props.csrfToken })
      .then(({ data }) => {
        setItem(data);
      });
  };

  const unsign = (event) => {
    event.preventDefault();

    axios.post(`/feedback/${item.id}/unsign`, { _token: window.props.csrfToken })
      .then(({ data }) => {
        setItem(data);
      });
  };

  if (thisItem.users.length === 0) {
    button = <button type="button" className="feedback-first-sign" onClick={sign} title="This will publicly show your support and let us know you're happy to talk about it.">I support this, and am happy to provide more feedback</button>;
  } else if (thisItem.user_has_signed) {
    button = <button type="button" className="feedback-unsign" onClick={unsign}>Remove me</button>;
  } else {
    button = <button type="button" className="feedback-sign" onClick={sign} title="I support this, and am happy to provide more feedback.">+</button>;
  }

  return (
    <>
      {thisItem.users.map((user) => <UserIcon className="mx-1" key={user.id} user={user} size="20px" />)}
      {button}
    </>
  );
}

function FeedbackVotes({ item }) {
  const [voteCount, setVoteCount] = useState(item.vote_count);
  const [hasVoted, setHasVoted] = useState(localStorage.getItem(`has_voted_${item.id}`));
  const [loading, setLoading] = useState(false);

  const addVote = (event) => {
    event.preventDefault();
    setLoading(true);
    setHasVoted(true);
    localStorage.setItem(`has_voted_${item.id}`, 'yes');

    axios.post(`/feedback/${item.id}/add-vote`, { _token: window.props.csrfToken })
      .then(({ data }) => {
        setLoading(false);
        setVoteCount(data.vote_count);
      })
      .catch((error) => {
        setLoading(false);
        alert('Sorry, there was a problem adding your vote. :(');
        console.log(error);
      });
  };

  const delVote = (event) => {
    event.preventDefault();
    setLoading(true);
    setHasVoted(false);
    localStorage.removeItem(`has_voted_${item.id}`);

    axios.post(`/feedback/${item.id}/del-vote`, { _token: window.props.csrfToken })
      .then(({ data }) => {
        setLoading(false);
        setVoteCount(data.vote_count);
      })
      .catch((error) => {
        setLoading(false);
        alert('Sorry, there was a problem removing your vote. :(');
        console.log(error);
      });
  };

  let button = <button type="button" className="vote-button" onClick={addVote}>Vote</button>;

  if (hasVoted) {
    button = <button type="button" className="vote-button active" onClick={delVote}>+1</button>;
  }

  return (
    <div
      style={{
        margin: 0,
        minWidth: '60px',
        border: '1px solid #ddd',
        position: 'relative',
      }}
      className={hasVoted ? 'text-success' : ''}
    >
      <LoadingAnimation loading={loading} />
      <div style={{ fontSize: '1.2em', textAlign: 'center', padding: '15px' }}>{voteCount}</div>
      {button}
    </div>
  );
}

function AddFeedback({
  topic, setLoading, setItems, showAddFeedback, setShowAddFeedback,
}) {
  const titleRef = useRef();
  const descriptionRef = useRef();
  const typeRef = useRef();

  const addFeedback = (event) => {
    event.preventDefault();
    setLoading(true);
    setShowAddFeedback(false);

    axios.post(`/feedback/${topic.slug}`, {
      _token: window.props.csrfToken,
      title: titleRef.current.value,
      description: descriptionRef.current.value,
      type: typeRef.current.value,
    })
      .then(({ data }) => {
        setLoading(false);
        setItems(data);
      })
      .catch((error) => {
        setLoading(false);
        alert('Sorry, there was a problem adding your feedback. :(');
        console.log(error);
      });
  };

  return (
    <Modal size="lg" show={showAddFeedback} onHide={() => setShowAddFeedback(null)}>
      <Modal.Header closeButton>
        <Modal.Title>Add Feedback</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="alert alert-success">
          <div>
            <Icon icon="comments-alt" variant="fad" append="mr-1" />
            {' '}
            <strong>
              Submitting feedback and voting on feedback given by others is completely anonymous.
            </strong>
          </div>
          <div>
            If you are happy to talk about a specific piece of feedback in more detail,
            you can optionally publicly support it so we know who to talk to.
          </div>
        </div>
        <Form onSubmit={addFeedback}>
          <div className="form-group">
            <label>Title</label>
            <input type="text" className="form-control" ref={titleRef} />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea className="form-control" ref={descriptionRef} />
          </div>

          <div className="form-group">
            <label>Type</label>

            <select ref={typeRef} className="form-control">
              <option value="suggestion">Suggestion or Idea</option>
              <option value="positive">Positive Feedback</option>
              <option value="negative">Constructive Criticism</option>
            </select>
          </div>

          <div className="mt-2" style={{ textAlign: 'right' }}>
            <Button type="submit" variant="outline-success">Submit Feedback</Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default FeedbackTopicPage;
